/**
 * The app-open audit record of each routed non-root mount with a catalog id (issue #4407; plan
 * 2026-10-21-app-open-audit D1, D6, D7, D11; plan 2026-10-22-app-open-hosted-root).
 *
 * The provider calls {@link recordAppOpen} beside the recents record, for each routed non-root
 * mount whose catalog id is not `null`. So the apps that a root shell mounts get a record under
 * the hosted `default-shell` and under the interim root alike. No root shell owns the call. A
 * root mount and an embedded mount write no record.
 */
import { APP_OPEN_ROUTE, appOpenRequestSchema } from '@zion/sdk-contracts';

/**
 * The rejection handler of the record. The record is fire and forget (D11). An audit write never
 * blocks or fails an open, so the record adds no retry of its own, no alert and no console line.
 */
function dropAppOpenError(): void {}

/**
 * Tell core-api that the signed-in person opened one app. core-api then writes one `app_opened`
 * audit row, with the person and the org from the verified bearer.
 *
 * Caller: `createSurfaceServices().servicesFor()`, once per routed non-root mount whose `appUuid`
 * is not `null`.
 *
 * - Input: `platformFetch` is the fetch of the origin policy, which attaches the session bearer.
 *   It is `sharedAuthFetch` on the shell origin and the kernel fetch on the kernel origin.
 *   `appUuid` is the app's `release.apps_catalog` id, `SurfaceMountRequest.appUuid`, never the
 *   slug.
 * - A value that `appOpenRequestSchema` refuses (not a UUID) sends nothing, so a bad id costs no
 *   400 round trip.
 * - Otherwise it sends one `POST` to `APP_OPEN_ROUTE` (`/v1/auth/app-opens`) with the body
 *   `{ appId }` and `content-type: application/json`. The init sets `keepalive: true`, so the
 *   browser can finish the request after the document unloads.
 * - It returns at once and never throws. {@link dropAppOpenError} takes a synchronous throw of
 *   `platformFetch` and a rejection. An answer status resolves the promise, and the code ignores
 *   it. It reads no response body.
 */
export function recordAppOpen(platformFetch: typeof fetch, appUuid: string): void {
  const parsed = appOpenRequestSchema.safeParse({ appId: appUuid });
  if (!parsed.success) return;
  // The executor turns a synchronous throw of the fetch into a rejection, so the catch drops it too.
  new Promise<Response>((resolve) => {
    resolve(
      platformFetch(APP_OPEN_ROUTE, {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(parsed.data),
      }),
    );
  }).catch(dropAppOpenError);
}
